import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface YouTubeVideoProps {
    videoURL: string;
    title?: string;
    className?: string;
    autoplay?: boolean;
    muted?: boolean;
    showControls?: boolean;
}

const YouTubeVideo = ({
    videoURL,
    title,
    className,
    autoplay = false,
    muted = false,
    showControls = true,
}: YouTubeVideoProps) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const buildYouTubeUrl = () => {
        const params = new URLSearchParams();

        if (autoplay) params.append("autoplay", "1");
        if (muted) params.append("mute", "1");
        if (!showControls) params.append("controls", "0");

        params.append("rel", "0"); // Do not show related videos
        params.append("modestbranding", "1"); // Reduce YouTube branding

        return `${videoURL}?${params.toString()}`;
    };

    const handleIframeLoad = () => {
        setIsLoading(false);
    };

    const handleIframeError = () => {
        setIsLoading(false);
        setHasError(true);
    };

    if (hasError) {
        return (
            <div
                className={cn(
                    "flex items-center justify-center rounded-lg bg-gray-100",
                    "p-8 text-center text-gray-500",
                    className,
                )}
            >
                <div>
                    <p className="text-sm">{t("errors.YouTubeVideo.title")}</p>
                    <p className="mt-2 text-xs">
                        {t("errors.YouTubeVideo.description")}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={cn("relative w-full", className)}>
            <div
                className="relative w-full"
                style={{ paddingBottom: "56.25%" }}
            >
                {isLoading && (
                    <div className="absolute inset-0 z-modal-backdrop flex items-center justify-center rounded-lg bg-gray-100">
                        <div className="border-primary-default h-8 w-8 animate-spin rounded-full border-b-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                    </div>
                )}

                <iframe
                    ref={iframeRef}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    className={cn(
                        "absolute left-0 top-0 h-full w-full rounded-lg",
                        isLoading && "opacity-0",
                    )}
                    frameBorder="0"
                    loading="lazy"
                    onError={handleIframeError}
                    onLoad={handleIframeLoad}
                    referrerPolicy="strict-origin-when-cross-origin"
                    src={buildYouTubeUrl()}
                    title={title}
                    allowFullScreen
                />
            </div>
        </div>
    );
};

export default YouTubeVideo;
